import React, { useState, useCallback, useMemo } from 'react';
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Drawer,
    Typography,
    IconButton,
    Tooltip,
    alpha
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

// Icons
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import TerminalIcon from '@mui/icons-material/TerminalOutlined';
import SpeedIcon from '@mui/icons-material/SpeedOutlined';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNoneOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Watchtower Logo SVG Component
const WatchtowerLogo = ({ size = 24 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        {/* Tower base */}
        <path
            d="M7 22V12H17V22"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />
        {/* Tower top cabin */}
        <path
            d="M5 12L12 6L19 12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />
        {/* Light beacon */}
        <circle
            cx="12"
            cy="9"
            r="2"
            fill="currentColor"
        />
        {/* Light rays */}
        <path
            d="M12 4V5.5M8 6L9 7M16 6L15 7"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
        {/* Windows */}
        <rect x="10" y="15" width="4" height="3" rx="0.5" fill="currentColor" opacity="0.6" />
    </svg>
);

// Static nav items moved outside component
const NAV_ITEMS = [
    { name: 'Query Runner', path: '/query_database', icon: <TerminalIcon /> }
];

const CLUSTERS_SUB_ITEMS = [
    { name: 'Overview', path: '/dashboard', icon: <DashboardIcon /> },
    { name: 'Settings', path: '/clusters', icon: <SettingsOutlinedIcon /> },
];

const CLUSTERS_PATHS = ['/dashboard', '/clusters'];

const PERFORMANCE_SUB_ITEMS = [
    { name: 'Notifications', path: '/notifications', icon: <NotificationsNoneIcon /> },
    { name: 'Settings', path: '/notification_settings', icon: <SettingsOutlinedIcon /> },
    { name: 'Thresholds', path: '/notifications/thresholds', icon: <TuneIcon /> },
    { name: 'Score Analysis', path: '/performance_alerts', icon: <AssessmentOutlinedIcon /> },
];

const PERFORMANCE_PATHS = ['/performance_alerts', '/notifications', '/notification_settings'];

const Sidebar = ({ width = 260, mobileOpen, onDrawerToggle, collapsed = false, onCollapse }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [clustersOpen, setClustersOpen] = useState(true);
    const [performanceOpen, setPerformanceOpen] = useState(true);

    // Memoize path checks
    const isSelected = useCallback((path) => location.pathname === path, [location.pathname]);
    const isInClustersSection = useMemo(
        () => CLUSTERS_PATHS.some(p => location.pathname === p || location.pathname.startsWith('/dashboard/')),
        [location.pathname]
    );
    const isInPerformanceSection = useMemo(
        () => PERFORMANCE_PATHS.some(p => location.pathname.startsWith(p)),
        [location.pathname]
    );

    // Memoize navigation handler
    const handleNavigate = useCallback((path) => {
        navigate(path);
    }, [navigate]);

    // Memoize toggle handlers
    const handleClustersToggle = useCallback(() => {
        if (!collapsed) {
            setClustersOpen(prev => !prev);
        }
    }, [collapsed]);

    const handlePerformanceToggle = useCallback(() => {
        if (!collapsed) {
            setPerformanceOpen(prev => !prev);
        }
    }, [collapsed]);

    const drawerContent = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
        }}>
            {/* Brand Area */}
            <Box sx={{
                p: collapsed ? 1.5 : 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minHeight: 64,
                borderBottom: '1px solid #F1F5F9',
            }}>
                <Box
                    onClick={() => handleNavigate('/dashboard')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        cursor: 'pointer',
                        flex: 1,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        '&:hover': {
                            opacity: 0.85,
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                            flexShrink: 0,
                            color: '#ffffff',
                        }}
                    >
                        <WatchtowerLogo size={22} />
                    </Box>
                    {!collapsed && (
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: '1.125rem',
                                color: '#0F172A',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Watchtower
                        </Typography>
                    )}
                </Box>
                {/* Collapse button - prominent placement */}
                {!collapsed && (
                    <IconButton
                        onClick={onCollapse}
                        size="small"
                        sx={{
                            color: '#64748B',
                            backgroundColor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            width: 28,
                            height: 28,
                            '&:hover': {
                                backgroundColor: '#F1F5F9',
                                color: '#0F172A',
                                borderColor: '#CBD5E1',
                            },
                        }}
                    >
                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                )}
            </Box>

            {/* Navigation */}
            <List sx={{ px: collapsed ? 0.75 : 1.5, pt: 2, flexGrow: 1, overflowY: 'auto' }}>
                {/* Clusters Section */}
                {collapsed ? (
                    /* Collapsed: Section label (non-clickable, bold, grey) */
                    <Tooltip title="Clusters (Section)" placement="right" arrow>
                        <Box sx={{
                            py: 1,
                            px: 0.5,
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'default',
                        }}>
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                backgroundColor: '#F1F5F9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #E2E8F0',
                            }}>
                                <StorageIcon sx={{ color: '#64748B', fontSize: 20 }} />
                            </Box>
                        </Box>
                    </Tooltip>
                ) : (
                    /* Expanded: Clickable section header */
                    <ListItem disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            onClick={handleClustersToggle}
                            sx={{
                                borderRadius: 2,
                                py: 1.25,
                                px: 2,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#F1F5F9',
                                },
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 40,
                                justifyContent: 'center',
                                color: isInClustersSection ? '#2563EB' : '#64748B'
                            }}>
                                <StorageIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Clusters"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isInClustersSection ? 600 : 500,
                                    color: isInClustersSection ? '#2563EB' : '#0F172A',
                                }}
                            />
                            {clustersOpen ? (
                                <ExpandLessIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            )}
                        </ListItemButton>
                    </ListItem>
                )}

                {/* Clusters Sub-items */}
                {collapsed ? (
                    <>
                        {/* Visual connector line for sub-items */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 0.5,
                        }}>
                            <Box sx={{
                                width: 2,
                                height: 8,
                                backgroundColor: '#E2E8F0',
                                borderRadius: 1,
                            }} />
                        </Box>
                        {CLUSTERS_SUB_ITEMS.map((subItem, idx) => {
                            const isActive = isSelected(subItem.path) || (subItem.path === '/dashboard' && location.pathname.startsWith('/dashboard/'));
                            return (
                                <ListItem key={subItem.name} disablePadding sx={{ mb: 0.5 }}>
                                    <Tooltip title={subItem.name} placement="right" arrow>
                                        <ListItemButton
                                            selected={isActive}
                                            onClick={() => handleNavigate(subItem.path)}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1,
                                                mx: 0.5,
                                                justifyContent: 'center',
                                                backgroundColor: isActive ? alpha('#2563EB', 0.1) : 'transparent',
                                                border: isActive ? '1px solid' : '1px solid transparent',
                                                borderColor: isActive ? alpha('#2563EB', 0.3) : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isActive ? alpha('#2563EB', 0.15) : '#F8FAFC',
                                                    borderColor: '#E2E8F0',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: 0,
                                                color: isActive ? '#2563EB' : '#64748B',
                                                '& svg': { fontSize: 20 }
                                            }}>
                                                {subItem.icon}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                        {/* Separator after section */}
                        <Box sx={{
                            mx: 1,
                            my: 1.5,
                            borderBottom: '1px solid #E2E8F0',
                        }} />
                    </>
                ) : (
                    <Collapse in={clustersOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1 }}>
                            {CLUSTERS_SUB_ITEMS.map((subItem) => {
                                const isSubActive = isSelected(subItem.path) || (subItem.path === '/dashboard' && location.pathname.startsWith('/dashboard/'));
                                return (
                                    <ListItemButton
                                        key={subItem.name}
                                        selected={isSubActive}
                                        onClick={() => handleNavigate(subItem.path)}
                                        sx={{
                                            pl: 4.5,
                                            py: 0.75,
                                            mb: 0.25,
                                            borderRadius: 1.5,
                                            '&:hover': { backgroundColor: '#F1F5F9' },
                                            '&.Mui-selected': {
                                                backgroundColor: alpha('#2563EB', 0.08),
                                                '&:hover': { backgroundColor: alpha('#2563EB', 0.12) },
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: 32,
                                            color: isSubActive ? '#2563EB' : '#94A3B8',
                                            '& svg': { fontSize: 18 }
                                        }}>
                                            {subItem.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={subItem.name}
                                            primaryTypographyProps={{
                                                fontSize: '0.8125rem',
                                                fontWeight: isSubActive ? 600 : 500,
                                                color: isSubActive ? '#2563EB' : '#475569',
                                            }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Collapse>
                )}

                {NAV_ITEMS.map((item) => {
                    const isActive = isSelected(item.path);
                    return (
                        <ListItem key={item.name} disablePadding sx={{ mb: 0.5 }}>
                            <Tooltip title={collapsed ? item.name : ''} placement="right" arrow>
                                <ListItemButton
                                    selected={isActive}
                                    onClick={() => handleNavigate(item.path)}
                                    sx={{
                                        borderRadius: 2,
                                        py: 1,
                                        mx: collapsed ? 0.5 : 0,
                                        px: collapsed ? 0 : 2,
                                        justifyContent: collapsed ? 'center' : 'flex-start',
                                        transition: 'all 0.15s ease',
                                        backgroundColor: isActive ? '#2563EB' : 'transparent',
                                        border: collapsed ? '1px solid transparent' : 'none',
                                        '&:hover': {
                                            backgroundColor: isActive ? '#1D4ED8' : '#F8FAFC',
                                            borderColor: collapsed ? '#E2E8F0' : 'transparent',
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: collapsed ? 0 : 40,
                                        justifyContent: 'center',
                                        color: isActive ? '#ffffff' : '#64748B',
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    {!collapsed && (
                                        <ListItemText
                                            primary={item.name}
                                            primaryTypographyProps={{
                                                fontSize: '0.875rem',
                                                fontWeight: isActive ? 600 : 500,
                                                color: isActive ? '#ffffff' : '#0F172A',
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </Tooltip>
                        </ListItem>
                    );
                })}

                {/* Performance Section */}
                {collapsed ? (
                    /* Collapsed: Section label (non-clickable, bold, grey) */
                    <Tooltip title="Performance (Section)" placement="right" arrow>
                        <Box sx={{
                            py: 1,
                            px: 0.5,
                            mt: 1,
                            mb: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'default',
                        }}>
                            <Box sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                backgroundColor: '#F1F5F9',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '2px solid #E2E8F0',
                            }}>
                                <SpeedIcon sx={{ color: '#64748B', fontSize: 20 }} />
                            </Box>
                        </Box>
                    </Tooltip>
                ) : (
                    /* Expanded: Clickable section header */
                    <ListItem disablePadding sx={{ mb: 0.5, mt: 1 }}>
                        <ListItemButton
                            onClick={handlePerformanceToggle}
                            sx={{
                                borderRadius: 2,
                                py: 1.25,
                                px: 2,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: '#F1F5F9',
                                },
                            }}
                        >
                            <ListItemIcon sx={{
                                minWidth: 40,
                                justifyContent: 'center',
                                color: isInPerformanceSection ? '#2563EB' : '#64748B'
                            }}>
                                <SpeedIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Performance"
                                primaryTypographyProps={{
                                    fontSize: '0.875rem',
                                    fontWeight: isInPerformanceSection ? 600 : 500,
                                    color: isInPerformanceSection ? '#2563EB' : '#0F172A',
                                }}
                            />
                            {performanceOpen ? (
                                <ExpandLessIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: '#94A3B8', fontSize: 20 }} />
                            )}
                        </ListItemButton>
                    </ListItem>
                )}

                {/* Performance Sub-items */}
                {collapsed ? (
                    <>
                        {/* Visual connector line for sub-items */}
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 0.5,
                        }}>
                            <Box sx={{
                                width: 2,
                                height: 8,
                                backgroundColor: '#E2E8F0',
                                borderRadius: 1,
                            }} />
                        </Box>
                        {PERFORMANCE_SUB_ITEMS.map((subItem) => {
                            const isActive = isSelected(subItem.path);
                            return (
                                <ListItem key={subItem.name} disablePadding sx={{ mb: 0.5 }}>
                                    <Tooltip title={subItem.name} placement="right" arrow>
                                        <ListItemButton
                                            selected={isActive}
                                            onClick={() => handleNavigate(subItem.path)}
                                            sx={{
                                                borderRadius: 2,
                                                py: 1,
                                                mx: 0.5,
                                                justifyContent: 'center',
                                                backgroundColor: isActive ? alpha('#2563EB', 0.1) : 'transparent',
                                                border: isActive ? '1px solid' : '1px solid transparent',
                                                borderColor: isActive ? alpha('#2563EB', 0.3) : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isActive ? alpha('#2563EB', 0.15) : '#F8FAFC',
                                                    borderColor: '#E2E8F0',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                minWidth: 0,
                                                color: isActive ? '#2563EB' : '#64748B',
                                                '& svg': { fontSize: 20 }
                                            }}>
                                                {subItem.icon}
                                            </ListItemIcon>
                                        </ListItemButton>
                                    </Tooltip>
                                </ListItem>
                            );
                        })}
                    </>
                ) : (
                    // Expanded: show in collapse
                    <Collapse in={performanceOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pl: 1 }}>
                            {PERFORMANCE_SUB_ITEMS.map((subItem) => (
                                <ListItemButton
                                    key={subItem.name}
                                    selected={isSelected(subItem.path)}
                                    onClick={() => handleNavigate(subItem.path)}
                                    sx={{
                                        pl: 4.5,
                                        py: 0.75,
                                        mb: 0.25,
                                        borderRadius: 1.5,
                                        '&:hover': { backgroundColor: '#F1F5F9' },
                                        '&.Mui-selected': {
                                            backgroundColor: alpha('#2563EB', 0.08),
                                            '&:hover': { backgroundColor: alpha('#2563EB', 0.12) },
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: 32,
                                        color: isSelected(subItem.path) ? '#2563EB' : '#94A3B8',
                                        '& svg': { fontSize: 18 }
                                    }}>
                                        {subItem.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={subItem.name}
                                        primaryTypographyProps={{
                                            fontSize: '0.8125rem',
                                            fontWeight: isSelected(subItem.path) ? 600 : 500,
                                            color: isSelected(subItem.path) ? '#2563EB' : '#475569',
                                        }}
                                    />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>
                )}
            </List>

            {/* Footer */}
            <Box sx={{
                p: 1.5,
                borderTop: '1px solid #F1F5F9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'space-between',
            }}>
                {collapsed ? (
                    /* Collapsed: Prominent expand button */
                    <Tooltip title="Expand sidebar" placement="right" arrow>
                        <IconButton
                            onClick={onCollapse}
                            sx={{
                                color: '#fff',
                                backgroundColor: '#2563EB',
                                width: 36,
                                height: 36,
                                boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                                '&:hover': {
                                    backgroundColor: '#1D4ED8',
                                },
                            }}
                        >
                            <ChevronRightIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                ) : (
                    /* Expanded: Version text only (collapse button moved to header) */
                    <Typography variant="caption" sx={{ color: '#94A3B8', fontSize: '0.6875rem' }}>
                        v0.1.0
                    </Typography>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={onDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: 260,
                        border: 'none',
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Desktop Drawer */}
            <Drawer
                variant="permanent"
                sx={{
                    display: { xs: 'none', md: 'flex' },
                    width: width,
                    flexShrink: 0,
                    transition: 'width 0.2s ease',
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: width,
                        borderRight: '1px solid #E2E8F0',
                        transition: 'width 0.2s ease',
                    },
                }}
                open
            >
                {drawerContent}
            </Drawer>
        </>
    );
};

export default Sidebar;
